import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageSquare, AlertCircle, CheckCircle, Download, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function Account() {
  const navigate = useNavigate();
  const { user, tenant, logout } = useAuth();
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [deletionConfirmation, setDeletionConfirmation] = useState('');
  const [deletionPassword, setDeletionPassword] = useState('');
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_new_password: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const res = await api.get('/billing/subscription');
      setSubscription(res.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      await api.post('/billing/cancel');
      toast.success(t('account.cancelSuccess'));
      setShowCancelDialog(false);
      await fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('account.cancelError'));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDataExport = async () => {
    setExportLoading(true);
    try {
      const response = await api.get('/account/data-export');
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `eva-data-export-${tenant?.id || 'account'}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('account.dataExportSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('account.dataExportError'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeletionRequest = async () => {
    setDeletionLoading(true);
    try {
      await api.post('/account/deletion-request', {
        confirmation: deletionConfirmation,
        current_password: deletionPassword,
      });
      toast.success(t('account.deletionRequestSuccess'));
      setShowDeletionDialog(false);
      setDeletionConfirmation('');
      setDeletionPassword('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || t('account.deletionRequestError'));
    } finally {
      setDeletionLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validation
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_new_password) {
      toast.error(t('auth.fillAllFields'));
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_new_password) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }

    setPasswordLoading(true);

    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });

      toast.success(t('auth.passwordChanged'));
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_new_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.wrongCurrentPassword'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Europe/Zurich',
      });
    } catch (error) {
      return '—';
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { variant: 'success', text: t('account.active') },
      incomplete: { variant: 'default', text: t('account.incompleteStatus') },
      past_due: { variant: 'warning', text: t('account.pastDue') },
      canceled: { variant: 'error', text: t('account.canceledStatus') },
    };

    const config = variants[status] || variants.incomplete;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const isInternal = subscription?.plan === 'internal';
  const isActive = subscription?.subscription_status === 'active';
  const canCancel = subscription?.plan === 'standard' && isActive && !subscription?.cancel_at_period_end;

  return (
    <div className='min-h-screen bg-gray-50' data-testid='account-page'>
      {/* Header */}
      <header className='border-b border-gray-200 bg-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 py-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-2'>
              <MessageSquare className='w-6 h-6 text-emerald-600' />
              <span className='text-2xl font-bold text-slate-900'>{t('account.title')}</span>
            </div>
            <div className='flex items-center space-x-3'>
              <LanguageSelector />
              <span className='text-sm text-slate-600 hidden sm:inline'>{user?.email}</span>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => navigate('/dashboard')}
                data-testid='button-dashboard'
              >
                {t('common.dashboard')}
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={logout}
                data-testid='button-logout'
              >
                {t('common.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className='max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8'>
        {/* Subscription Card */}
        <Card data-testid='card-subscription'>
          <CardHeader>
            <CardTitle>{t('account.subscription')}</CardTitle>
            <CardDescription>{t('account.subscriptionDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingSubscription ? (
              <p className='text-slate-500'>{t('common.loading')}</p>
            ) : (
              <div className='space-y-6'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <p className='text-sm text-slate-600 mb-2'>{t('account.plan')}</p>
                    <Badge variant={isInternal ? 'info' : 'default'} className='text-sm'>
                      {isInternal ? t('account.founderInternal') : t('account.standard')}
                    </Badge>
                  </div>
                  <div>
                    <p className='text-sm text-slate-600 mb-2'>{t('account.status')}</p>
                    {getStatusBadge(subscription?.subscription_status)}
                  </div>
                </div>

                {!isInternal && subscription?.current_period_end && (
                  <div className='space-y-3'>
                    <div className='flex items-center justify-between py-2 border-t border-slate-200'>
                      <span className='text-sm text-slate-600'>{t('account.nextRenewal')}</span>
                      <span className='text-sm font-medium text-slate-900'>
                        {formatDate(subscription.current_period_end)}
                      </span>
                    </div>
                    <div className='flex items-center justify-between py-2'>
                      <span className='text-sm text-slate-600'>{t('account.scheduledCancel')}</span>
                      <Badge variant={subscription?.cancel_at_period_end ? 'warning' : 'default'} className='text-xs'>
                        {subscription?.cancel_at_period_end ? t('common.yes') : t('common.no')}
                      </Badge>
                    </div>
                  </div>
                )}

                {subscription?.cancel_at_period_end && !isInternal && (
                  <Alert variant='warning'>
                    <AlertCircle className='h-4 w-4' />
                    <AlertDescription>
                      {t('account.cancelWarning', { date: formatDate(subscription.current_period_end) })}
                    </AlertDescription>
                  </Alert>
                )}

                {isInternal ? (
                  <Alert className='border-emerald-200 bg-emerald-50'>
                    <CheckCircle className='h-4 w-4 text-emerald-600' />
                    <AlertDescription className='text-emerald-800'>
                      <strong>Founder plan:</strong> {t('account.founderAlert')}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <div className='bg-slate-50 rounded-lg p-4 border border-slate-200'>
                      <div className='flex items-center justify-between mb-2'>
                        <span className='text-sm text-slate-600'>{t('account.price')}</span>
                        <span className='text-lg font-semibold text-slate-900'>99 CHF/{t('wizard.perMonth')}</span>
                      </div>
                      {subscription?.current_period_end && (
                        <div className='flex items-center justify-between'>
                          <span className='text-sm text-slate-600'>{t('account.validUntil')}</span>
                          <span className='text-sm font-medium text-slate-900'>
                            {formatDate(subscription.current_period_end)}
                          </span>
                        </div>
                      )}
                      {subscription?.cancel_at_period_end && (
                        <div className='mt-3 pt-3 border-t border-slate-300'>
                          <Alert variant='warning'>
                            <AlertCircle className='h-4 w-4' />
                            <AlertDescription>
                              <strong>{t('account.scheduledCancel')}:</strong> {t('account.scheduledCancelAlert')}
                            </AlertDescription>
                          </Alert>
                        </div>
                      )}
                    </div>

                    {canCancel && (
                      <Button
                        variant='destructive'
                        onClick={() => setShowCancelDialog(true)}
                        data-testid='button-cancel-subscription'
                      >
                        {t('account.cancelSubscription')}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid='card-data-lifecycle'>
          <CardHeader>
            <CardTitle>{t('account.yourData')}</CardTitle>
            <CardDescription>{t('account.yourDataDesc')}</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <Alert>
              <AlertDescription>{t('account.dataLifecycleNotice')}</AlertDescription>
            </Alert>
            <div className='flex flex-col sm:flex-row gap-3'>
              <Button
                variant='outline'
                onClick={handleDataExport}
                disabled={exportLoading}
                data-testid='button-data-export'
              >
                <Download className='w-4 h-4 mr-2' />
                {exportLoading ? t('common.loading') : t('account.downloadData')}
              </Button>
              {!isInternal && (
                <Button
                  variant='destructive'
                  onClick={() => setShowDeletionDialog(true)}
                  data-testid='button-request-deletion'
                >
                  <Trash2 className='w-4 h-4 mr-2' />
                  {t('account.requestDeletion')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card data-testid='card-change-password'>
          <CardHeader>
            <CardTitle>{t('account.security')}</CardTitle>
            <CardDescription>{t('account.securityDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='current_password'>{t('auth.currentPassword')}</Label>
                <Input
                  id='current_password'
                  name='current_password'
                  type='password'
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  data-testid='input-current-password'
                  disabled={passwordLoading}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='new_password'>{t('auth.newPassword')}</Label>
                <Input
                  id='new_password'
                  name='new_password'
                  type='password'
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  data-testid='input-new-password'
                  disabled={passwordLoading}
                />
                <p className='text-xs text-slate-500'>{t('auth.minChars')}</p>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='confirm_new_password'>{t('auth.confirmNewPassword')}</Label>
                <Input
                  id='confirm_new_password'
                  name='confirm_new_password'
                  type='password'
                  value={passwordForm.confirm_new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_new_password: e.target.value })}
                  data-testid='input-confirm-new-password'
                  disabled={passwordLoading}
                />
              </div>

              <Button
                type='submit'
                disabled={passwordLoading}
                data-testid='button-change-password'
              >
                {passwordLoading ? t('auth.changingPassword') : t('auth.changePassword')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showDeletionDialog} onOpenChange={setShowDeletionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('account.deletionConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('account.deletionConfirmDesc')}</DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='deletion_confirmation'>{t('account.deletionConfirmationLabel')}</Label>
              <Input
                id='deletion_confirmation'
                value={deletionConfirmation}
                onChange={(e) => setDeletionConfirmation(e.target.value)}
                placeholder='DELETE MY EVA ACCOUNT'
                data-testid='input-deletion-confirmation'
              />
            </div>
            {user?.auth_providers?.includes('password') && (
              <div className='space-y-2'>
                <Label htmlFor='deletion_password'>{t('auth.currentPassword')}</Label>
                <Input
                  id='deletion_password'
                  type='password'
                  value={deletionPassword}
                  onChange={(e) => setDeletionPassword(e.target.value)}
                  data-testid='input-deletion-password'
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowDeletionDialog(false)} disabled={deletionLoading}>
              {t('common.cancel')}
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeletionRequest}
              disabled={deletionLoading || deletionConfirmation !== 'DELETE MY EVA ACCOUNT'}
              data-testid='button-confirm-deletion-request'
            >
              {deletionLoading ? t('common.loading') : t('account.submitDeletionRequest')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('account.cancelConfirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('account.cancelConfirmDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setShowCancelDialog(false)}
              disabled={cancelLoading}
              data-testid='button-cancel-dialog'
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='destructive'
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
              data-testid='button-confirm-cancel'
            >
              {cancelLoading ? t('account.canceling') : t('account.confirmCancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
